import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseIcon, CalendarIcon, CheckIcon, ClockIcon, GraduationCapIcon, LayersIcon, MapPinIcon, MonitorIcon, TrendingUpIcon, UserIcon } from 'lucide-react';
import { getCareerRoleDetail, getDomain, getProgramHeroImage, getProgramMatch, getProgramsByDomain, programsIndexSlug, type ProgramTemplate } from '../data/programTemplates';
import { buildMatchLink } from '../utils/matchParams';
import { HeroMatchForm } from '../components/HeroMatchForm';
import { LegalFooter } from '../components/LegalFooter';
import { usePageMeta } from '../hooks/usePageMeta';
interface ProgramTemplatePageProps {
  program: ProgramTemplate;
}
export function ProgramTemplatePage({
  program
}: ProgramTemplatePageProps) {
  const domain = getDomain(program.domainSlug);
  const siblings = getProgramsByDomain(program.domainSlug);
  const related = siblings.filter(p => p.slug !== program.slug);
  const heroImage = getProgramHeroImage(program.slug);
  // The contact step has the most fields, so the form widens once the visitor
  // clears the ZIP question rather than squeezing them into a narrow column.
  const [formExpanded, setFormExpanded] = useState(false);
  const handleFormStep = useCallback((step: number) => {
    setFormExpanded(step >= 3);
  }, []);
  usePageMeta(program.metaTitle, program.metaDescription, {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [program.slug]);

  // Fallback path for visitors who scroll past the hero form.
  const matchHref = buildMatchLink('/request-information', getProgramMatch(program));
  const scrollToForm = () => {
    document.getElementById('hero-match-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const stats = [{
    icon: CalendarIcon,
    label: 'Program Length',
    value: `${program.weeks} weeks`
  }, {
    icon: LayersIcon,
    label: 'Credits',
    value: `${program.credits} credits`
  }, {
    icon: ClockIcon,
    label: 'Instructional Hours',
    value: program.instructionalHours.toLocaleString()
  }, {
    icon: MonitorIcon,
    label: 'Format',
    value: program.format
  }];
  return <main className="min-h-screen bg-white" data-id="element-2588" data-mp="1inulz7eqgwtv">
      {/* Hero. The photograph shows the work this program leads to; the navy
          base and scrim keep the headline legible while it loads and at widths
          where the image doesn't fully cover. */}
      <section className="relative bg-navy overflow-hidden" data-id="element-2589" data-mp="1woozg4gn1nze">
        {heroImage && <>
            {/* Mirrored so the photo's subject sits on the left, in the open
                band beside the headline, instead of behind the form card. */}
            <img src={heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center lg:scale-x-[-1]" data-id="element-2590" data-mp="26d6v67a294w6" />
            <div className="absolute inset-0 bg-navy/80" aria-hidden="true" data-id="element-2591" data-mp="2ff6cl0ozmazn" />
          </>}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7 lg:py-20" data-id="element-2592" data-mp="2fctxcvam6eqa">
          <nav aria-label="Breadcrumb" className="hidden lg:block mb-6" data-id="element-2593" data-mp="3pqepji5jjyh">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2594" data-mp="3uxcq5yb44qpw">
              <li data-id="element-2595" data-mp="3qxkdpailm0f9">
                <Link to={`/program-information/${programsIndexSlug}`} className="hover:text-white transition-colors duration-200" data-id="element-2596" data-mp="3qx2j0ibl3u9g">
                  New Online Programs
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2597" data-mp="mhj6xbped155">/</li>
              <li className="text-white/90" data-id="element-2598" data-mp="igz883p6dpgw">{domain?.name}</li>
            </ol>
          </nav>

          {/* Explicit grid placement rather than a duplicated form: on mobile
              the form follows the headline so it lands within reach, and the
              supporting trust points move below it. */}
          <div className="grid gap-y-5 lg:grid-cols-5 lg:gap-y-7 lg:gap-x-14 items-start" data-id="element-2599" data-mp="166tjwhxrd7zk">
            <div className={`${formExpanded ? 'lg:col-span-5 lg:col-start-1' : 'lg:col-span-3 lg:col-start-1'} lg:row-start-1`} data-id="element-2600" data-mp="11f1vacy5xcc2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold uppercase tracking-widest text-gold mb-3 lg:mb-5" data-id="element-2601" data-mp="kzavm1cu6tsu">
                <GraduationCapIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2602" data-mp="c0pfsdyr1svr" />
                {program.credential}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-2603" data-mp="h0btspmejd5m">
                {program.heroHeadline}
              </h1>
            </div>

            {/* Once the ZIP step is behind them the visitor is filling in
                contact details, so the form takes the full width and the
                supporting copy moves out of its way. */}
            <div id="hero-match-form" className={formExpanded ? 'lg:col-span-5 lg:col-start-1 lg:row-start-2' : 'lg:col-span-2 lg:col-start-4 lg:row-start-1 lg:row-span-2'} data-id="element-2604" data-mp="zviff0kc1t7l">
              <HeroMatchForm programs={siblings.length > 0 ? siblings : [program]} initialProgramSlug={program.slug} onStepChange={handleFormStep} wide={formExpanded} data-id="element-2605" data-mp="1sepa99etsqbm" />
            </div>

            <p className={`${formExpanded ? 'lg:col-span-5 lg:col-start-1 lg:row-start-3' : 'lg:col-span-3 lg:col-start-1 lg:row-start-2'} text-base lg:text-lg text-white/80 leading-relaxed max-w-2xl`} data-id="element-2606" data-mp="1xrnpxgy3wavi">
              {program.heroSubhead}
            </p>

            <ul className={`${formExpanded ? 'lg:col-span-5 lg:col-start-1 lg:row-start-4' : 'lg:col-span-3 lg:col-start-1 lg:row-start-3'} flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80`} style={{
            gap: "10px",
            alignItems: "center",
            justifyContent: "center"
          }} data-id="element-2607" data-mp="26limi0l5wszj">
              <li className="flex items-center gap-2" data-id="element-2608" data-mp="1stcfp53wfycr">
                <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2609" data-mp="1ns59hahen787" />
                {program.format}
              </li>
              <li className="flex items-center gap-2" data-id="element-2610" data-mp="345dap1rcwj0y">
                <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2611" data-mp="2z8d2ak71b46o" />
                {program.weeks} weeks
              </li>
              <li className="flex items-center gap-2" data-id="element-2612" data-mp="3789n4duq48ug">
                <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2613" data-mp="3b9ldillf8y3p" />
                No cost to get matched
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stat row */}
      <section className="border-b border-gray-200 bg-light" data-id="element-2614" data-mp="3l7sie62frppc">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2615" data-mp="2nl276blxmve9">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2616" data-mp="2niph1b60teq4">
            {stats.map(stat => <div key={stat.label} className="flex items-start gap-3" style={{
            alignItems: "center",
            justifyContent: "center"
          }} data-id="element-2617" data-mp="2shk9z5xpcjny">
                <stat.icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2618" data-mp="3296j8wqhc8o2" />
                <div data-id="element-2619" data-mp="24uc7evrnropm">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-2620" data-mp="24t12gsmh8awx">
                    {stat.label}
                  </dt>
                  <dd className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-2621" data-mp="229mieyi3ktqt">
                    {stat.value}
                  </dd>
                </div>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2622" data-mp="1sankoayveiil">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-2623" data-mp="1ncdlzqdxszs8">
          <div className="lg:col-span-2 space-y-12" data-id="element-2624" data-mp="1nbkfmqiw4cam">
            <section aria-labelledby="overview-heading" data-id="element-2625" data-mp="1jaicvzprli3w">
              <h2 id="overview-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2626" data-mp="1jc35vkwfkszn">
                Program Overview
              </h2>
              <div className="space-y-4" data-id="element-2627" data-mp="19kpc7tr5unef">
                {program.overview.map((paragraph, idx) => <p key={idx} className="text-gray-700 leading-relaxed" data-id="element-2628" data-mp="19imfyrzezy19" data-mpi={idx}>
                    {paragraph}
                  </p>)}
              </div>
            </section>

            {program.concentrations && <section aria-labelledby="concentrations-heading" data-id="element-2629" data-mp="zolyrrquz2c6">
                <h2 id="concentrations-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2630" data-mp="vlpp4qjaqgkd">
                  Program Concentrations
                </h2>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-2631" data-mp="2ohi8dov96a2c">
                  {program.concentrations.map(c => <div key={c.name} className="border border-gray-200 rounded-xl p-5 flex flex-col" data-id="element-2632" data-mp="29jy64ppqqszv">
                      <h3 className="font-bold text-navy mb-2" data-id="element-2633" data-mp="2ei9hx5zljdkq">{c.name}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2634" data-mp="2ekluf3ml4vqe">
                        {c.description}
                      </p>
                    </div>)}
                </div>
              </section>}

            <section aria-labelledby="features-heading" data-id="element-2635" data-mp="1qtgigzjrmnld">
              <h2 id="features-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2636" data-mp="1qurj4qhwzf89">
                Unique Program Features
              </h2>
              <ul className="space-y-3" data-id="element-2637" data-mp="1hss609a0sval">
                {program.features.map(feature => <li key={feature} className="flex items-start" data-id="element-2638" data-mp="1huw60g4sta5l">
                    <CheckIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-crimson" aria-hidden="true" data-id="element-2639" data-mp="1ltlrkvzzpy0b" />
                    <span className="text-gray-700" data-id="element-2640" data-mp="13636w8jokk4q">{feature}</span>
                  </li>)}
              </ul>
            </section>

            {program.studentProfile && <section aria-labelledby="profile-heading" data-id="element-2641" data-mp="1c6rkx392qkhm">
                <h2 id="profile-heading" className="text-2xl font-bold text-navy mb-4" data-id="element-2642" data-mp="1c451o6ctmgjk">
                  Who This Program Is For
                </h2>
                <div className="border-l-4 border-crimson pl-5" data-id="element-2643" data-mp="1m3nkgjgzvwa1">
                  <p className="text-gray-700 leading-relaxed" data-id="element-2644" data-mp="1h5m4w8is2f3b">
                    {program.studentProfile}
                  </p>
                </div>
              </section>}

          </div>

          {/* Sidebar. Sticky so it travels with the reader instead of leaving a
              tall empty column beside the long-form content. */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start" data-id="element-2645" data-mp="24w86pi26v34v">
            <div className="border border-gray-200 rounded-2xl p-6 bg-light" data-id="element-2646" data-mp="1zwlv1d20duy6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2647" data-mp="2k1qi5fv259np">
                Completion Program Estimate
              </h2>
              <dl className="space-y-3 text-sm" data-id="element-2648" data-mp="2f9gvc8p8oppn">
                <div className="flex justify-between gap-4" data-id="element-2649" data-mp="2b96pcw1g5zas">
                  <dt className="text-gray-500" data-id="element-2650" data-mp="2b9ohphkzntdf">Credential</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2651" data-mp="26b4y3ntvato8">
                    {program.credential}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2652" data-mp="32y9s27dor1lb">
                  <dt className="text-gray-500" data-id="element-2653" data-mp="2yzivc1d4tlyq">Length</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2654" data-mp="2tzent0u7c1kn">
                    {program.weeks} weeks
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2655" data-mp="2p2xpxex7a8ud">
                  <dt className="text-gray-500" data-id="element-2656" data-mp="1ilsmkhb2i9ir">Credits</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2657" data-mp="1dlo7mvpt5mun">
                    {program.credits}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2658" data-mp="13jathjmufnbe">
                  <dt className="text-gray-500" data-id="element-2659" data-mp="18gayrsbvcm1g">Hours</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2660" data-mp="18eq5s757db5p">
                    {program.instructionalHours.toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2661" data-mp="1i7yjzbhu16uh">
                  <dt className="text-gray-500" data-id="element-2662" data-mp="1m90jltfnvkwz">Program</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2663" data-mp="1male5j1z73uu">
                    {domain?.name}
                  </dd>
                </div>
              </dl>
              <button type="button" onClick={scrollToForm} className="mt-6 w-full bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm uppercase tracking-wide transition-colors duration-200" data-id="element-2664" data-mp="1w6y7ugxithpl">
                Get Matched Now
              </button>
            </div>

            {related.length > 0 && <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2665" data-mp="6rqmhoh9clpo">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2666" data-mp="3uwyf1g24q2rt">
                  Also in This Domain
                </h2>
                <ul className="space-y-3" data-id="element-2667" data-mp="8oyuuelu0cs7">
                  {related.map(p => <li key={p.slug} data-id="element-2668" data-mp="8qjlht50zbks">
                      <Link to={`/program-information/${p.slug}`} className="group flex items-start justify-between gap-3" data-id="element-2669" data-mp="3pvqyxhl8clt">
                        <span className="text-sm text-gray-700 group-hover:text-navy transition-colors duration-200" data-id="element-2670" data-mp="hovaxru5o3bn">
                          {p.name}
                          <span className="block text-xs text-gray-500" data-id="element-2671" data-mp="cops915v5r5p">
                            {p.credential}
                          </span>
                        </span>
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-2672" data-mp="mh5a7yh5ufql" />
                      </Link>
                    </li>)}
                </ul>
              </div>}

            <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2673" data-mp="1f2lid77i9mdq">
              <UserIcon className="w-6 h-6 mb-3 text-crimson" aria-hidden="true" data-id="element-2674" data-mp="1k1y3nniqale7" />
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2675" data-mp="1jzu0j7snlqez">
                Not sure which program fits? Compare all six program domains and
                find the pathway that matches your goals.
              </p>
              <Link to={`/program-information/${programsIndexSlug}`} className="mt-4 inline-flex items-center text-sm font-semibold text-crimson hover:underline" data-id="element-2676" data-mp="wclyqzufozhp">
                Browse all programs
                <ArrowRightIcon className="w-4 h-4 ml-1.5" aria-hidden="true" data-id="element-2677" data-mp="nbxhzxdnol14" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Careers. Full width, because the role cards need the room — a bare
          list of job titles tells a career changer nothing about whether the
          role suits them. */}
      <section aria-labelledby="careers-heading" className="border-t border-gray-200 bg-light" data-id="element-2678" data-mp="ib9l4v2qx9yz">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2679" data-mp="1kopavdzmci5x">
          <div className="max-w-3xl" data-id="element-2680" data-mp="1go5bs4vhj4h5">
            <div className="flex items-baseline justify-between gap-4 mb-4" data-id="element-2681" data-mp="1gp703b1ihngt">
              <h2 id="careers-heading" className="text-2xl font-bold text-navy" data-id="element-2682" data-mp="1bwnd5ngxpfzc">
                Careers to Explore
              </h2>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap" data-id="element-2683" data-mp="16whpeidscdmj">
                {program.careerRoles.length} roles
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed mb-8" data-id="element-2684" data-mp="x2ispywhdm1z">
              {program.careersIntro}
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2685" data-mp="o09ht7xavqo2">
            {program.careerRoles.map(role => {
            const detail = getCareerRoleDetail(role);
            return <li key={role} className="flex" data-id="element-2686" data-mp="2nahkng1orf3g">
                  <div className="w-full bg-white border border-gray-200 rounded-xl p-5 flex flex-col hover:border-navy transition-colors duration-200" data-id="element-2687" data-mp="2ieshz3uddi2x">
                    <div className="flex items-start gap-3" data-id="element-2688" data-mp="2uwd2rc8tjzcb">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-light flex items-center justify-center text-navy" aria-hidden="true" data-id="element-2689" data-mp="2uus9rr25kogk">
                        <BriefcaseIcon className="w-4 h-4" data-id="element-2690" data-mp="34lwmek4cvx88" />
                      </span>
                      <h3 className="text-sm font-bold text-navy leading-snug pt-1.5" data-id="element-2691" data-mp="26ye5un4gdqto">
                        {role}
                      </h3>
                    </div>
                    {detail && <>
                        <p className="mt-3 text-sm text-gray-600 leading-relaxed" data-id="element-2692" data-mp="2gwtwyq3hd036">
                          {detail.summary}
                        </p>
                        <p className="mt-auto pt-4 flex items-center gap-1.5 text-xs text-gray-500" data-id="element-2693" data-mp="2kzqhimckz69r">
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2694" data-mp="3sbh7p9nzf6ko" />
                          {detail.setting}
                        </p>
                      </>}
                  </div>
                </li>;
          })}
          </ul>

          <p className="mt-6 max-w-3xl text-xs text-gray-500 leading-relaxed" data-id="element-2695" data-mp="1s8g24b7g96bg">
            Roles listed are examples of where graduates commonly begin. Actual
            hiring, pay, and advancement vary by employer, location, and
            experience — check current openings and Bureau of Labor Statistics
            data for your area.
          </p>

          {program.marketRelevance && <div className="mt-10 pt-10 border-t border-gray-200 max-w-3xl" data-id="element-2696" data-mp="19jswnrld2exc">
              <h2 className="text-xl font-bold text-navy mb-3 flex items-center gap-2" data-id="element-2697" data-mp="1na4qpw1ianr4">
                <TrendingUpIcon className="w-5 h-5 text-crimson" aria-hidden="true" data-id="element-2698" data-mp="1ijodod1ll45a" />
                Market Relevance
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-2699" data-mp="utbzwljo8d6s">
                {program.marketRelevance}
              </p>
            </div>}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy py-14" data-id="element-2700" data-mp="31huhjvp24k93">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2701" data-mp="31h2sl3pytkrs">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2702" data-mp="369vz9x4zv36i">
            Ready to Take the Next Step?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-2703" data-mp="2sjiqjska3kg4">
            Get matched with accredited schools offering the {program.name}{' '}
            {program.credential.toLowerCase()} program. No cost, and no
            obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2704" data-mp="3p8gkx9o49gcr">
            <button type="button" onClick={scrollToForm} className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-10 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200" data-id="element-2705" data-mp="3b8fkfvbrkaxt">
              Get Matched Now
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2706" data-mp="1zv3dvcomde7y" />
            </button>
            
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-2707" data-mp="2wg7pxs0d4tt4" />
    </main>;
}